import "./OrderList.scss";

import React from "react";
import { Link } from "react-router-dom"
import usesFeatures from "usesFeatures"
import { formatDateTimeWithZone } from "date_helpers"
import { startCase } from "lodash"
import DatePicker from "DatePicker"
import moment from "moment-timezone"

import Table from "Table"
import Icon from "Icon"
import Header from "Header"
import Paginate from "Paginate"
import request from "lib/request"

const ORDER_HEADERS = {
  type: "",
  customerName: "Customer",
  status: "Status",
  method: "Method",
  accountName: "Account",
  storeName: "Store",
  total: "Total",
  lastFour: "Card",
  refunded: "Refunded?",
  placedAt: "Placed",
  view: "",
}
class OrderList extends React.Component {
  ORDER_STATUSES = ["complete", "cancelled"];

  static defaultProps = {
    dataSource: "internal/orders",
    tableHeaders: ORDER_HEADERS,
    storeInputType: "text" // "text" or "select"
  }

  state = {
    filters: {
      placedFrom: null,
      placedTo: null,
      // Internal has all the stores and uses text filter
      // Venue Admin has only the stores that the account( or user) has access to
      // and uses select filter
      storeId: "",
      storeName: "",
      status: "",
      refunded: "",
      customer: ""
    },
    stores: []
  }

  componentDidMount() {
    if (this.props.storeInputType === "select") {
      // I can't make fetchData to work with 2 different urls
      request.get("/venue_admin/stores").then(({ data }) => {
        this.setState({ stores: data['stores'] })
      })
    }
    this.props.fetchData(this.props.dataSource);
  }

  handleFilterChange = (field, value) => {
    this.setState(
      prev => ({
        filters: { ...prev.filters, [field]: value }
      }),
      this.fetchFilteredData
    );
  }

  filteredURL = () => {
    const params = { ...this.state.filters };
    // Remove empty values
    Object.keys(params).forEach(k => (params[k] === "" || params[k] == null) && delete params[k]);
    // Add browser timezone to params
    if (params.placedFrom || params.placedTo) {
      params.timezone = moment.tz.guess();
    }
    const query = new URLSearchParams(params).toString();
    const url = query ? `${this.props.dataSource}?${query}` : this.props.dataSource;
    return url
  }

  fetchFilteredData = () => {
    this.props.getPage(this.filteredURL(), 1);
  }

  orderFormatters = () => {
    return({
      type: (o) => <Icon icon={o.type == "terminal" ? "cash-register" : "mobile"} />,
      total: (o) => `$${o.total}`,
      status: (o) => startCase(o.status),
      method: (o) => o.hasDelivery ? "Delivery" : "Pickup",
      lastFour: (o) => o.payment?.lastFour ? `****${o.payment.lastFour}` : "",
      refunded: (o) => o.refundCount > 0 ? "Yes" : "No",
      placedAt: (o) => formatDateTimeWithZone(o.placedAt),
      view: (o) => <Link to={`/orders/${o.id}`} target="_blank" rel="noopener noreferrer">View</Link>
    })
  }

  changePage = (page) => {
    this.props.getPage(this.filteredURL(), page)
  }

  renderFilters() {
    const { filters, stores } = this.state;
    const statusOptions = this.props.statuses || this.ORDER_STATUSES;
    const storeInputType = this.props.storeInputType;

    return (
      <div className="Card s-card">
      <div className="columns is-multiline mb-1">
        <div className="column is-2">
          <label className="label">Customer</label>
          <input
            className="input"
            type="text"
            value={filters.customer}
            onChange={e => this.handleFilterChange("customer", e.target.value)}
            placeholder="name or last 4"
          />
        </div>

        <div className="column is-1 pl-0 pr-0">
          <label className="label">Status</label>
          <select
            className="input"
            value={filters.status}
            onChange={e => this.handleFilterChange("status", e.target.value)}
          >
            <option value="">All</option>
            {statusOptions.map(status => (
              <option key={status} value={status}>{startCase(status)}</option>
            ))}
          </select>
        </div>

        <div className="column is-2">
          <label className="label">Store</label>
          {storeInputType === "select" ? (
            <select
              className="input"
              value={filters.storeId}
              onChange={e => this.handleFilterChange("storeId", e.target.value)}
            >
              <option value="">All</option>
              {stores.map(store => (
                <option key={store.id} value={store.id}>{store.name}</option>
              ))}
            </select>
          ) : (
            <input
              className="input"
              type="text"
              value={filters.storeName}
              onChange={e => this.handleFilterChange("storeName", e.target.value)}
              placeholder="Store name"
            />
          )}
        </div>

        <div className="column is-1">
          <label className="label">Refunded</label>
          <select
            className="input"
            value={filters.refunded}
            onChange={e => this.handleFilterChange("refunded", e.target.value)}
          >
            <option value="">All</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>

        <div className="column is-3">
          <label className="label">Placed From ({moment.tz(moment.tz.guess()).format('z')})</label>
          <DatePicker
            value={filters.placedFrom}
            onChange={date => this.handleFilterChange("placedFrom", date)}
            placeholder="From"
            input_type="datetime"
          />
        </div>

        <div className="column is-3">
          <label className="label">Placed To ({moment.tz(moment.tz.guess()).format('z')})</label>
          <DatePicker
            value={filters.placedTo}
            onChange={date => this.handleFilterChange("placedTo", date)}
            placeholder="To"
            input_type="datetime"
          />
        </div>
      </div>
      </div>
    );
  }

  render() {
    if (!this.props.dataFetched) return false

    return(
      <div className="OrderList">
        <Header
          text="Orders"
          actions={true}
        />
        {this.renderFilters()}
        <Table
          showHeader={true}
          headers={this.props.tableHeaders}
          formatters={this.orderFormatters()}
          rows={this.props.pageData}
        />
        <Paginate
          changePage={this.changePage}
          {...this.props.pagy}
        />
      </div>
    )
  }
}

export default usesFeatures(["data", "pagination"], OrderList)
